import {
  DEFAULT_FILTER_MODEL_STATE,
  useDataGridContext,
  useIssueStatuses,
} from '@issuemanager/apps/client/components';
import { useUserContext } from '@issuemanager/apps/client/contexts';
import {
  ISSUE_FIELD_ACTION_PROGRESS,
  ISSUE_FIELD_DUE_DATE,
  ISSUE_FIELD_ISSUE_STATUS,
  ISSUE_FIELD_OWNER,
  IssueActionProgressEnum,
  OwnedIssuesTypeEnum,
} from '@issuemanager/constants';
import {
  GetIssueResponseDtoStatusEnum,
  OwnedIssuesDto,
} from '@issuemanager/api-client';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { OwnerColumnFilter } from '@issuemanager/client/features/issues';
import {
  OwnedIssuesEnum,
  QuickFilterAllEnum,
} from '@issuemanager/test/constants';
import { QuickFilterContainer } from './quick-filter-container';
import { QuickFilterItem } from './quick-filter-item';
import { useIssueActionProgress } from '../issue-action-progress-label';

export function OwnedIssues({
  ownedIssues,
  activeFilter,
  setActiveFilter,
}: {
  ownedIssues: OwnedIssuesDto | undefined;
  activeFilter: QuickFilterAllEnum | null;
  setActiveFilter: (value: QuickFilterAllEnum) => void;
}) {
  const context = useDataGridContext();
  const user = useUserContext();
  const actionProgress = useIssueActionProgress();
  const { t } = useTranslation('issue');
  const issueStatuses = useIssueStatuses();

  const userDataForFiltering = {
    ...user.data,
    group: OwnerColumnFilter.ISSUE_OWNER,
  };
  const clearFilters = () => {
    context.setFilterModel(DEFAULT_FILTER_MODEL_STATE);
  };
  const actionProgressNotInitiated = useMemo(
    () =>
      actionProgress.find(
        (a) => a.value === IssueActionProgressEnum.NOT_INITIATED,
      ),
    [actionProgress],
  );
  const actionProgressInitiated = useMemo(
    () =>
      actionProgress.find((a) => a.value === IssueActionProgressEnum.INITIATED),
    [actionProgress],
  );
  const actionProgressOngoing = useMemo(
    () =>
      actionProgress.find((a) => a.value === IssueActionProgressEnum.ONGOING),
    [actionProgress],
  );
  const issueStatusInProgress = useMemo(
    () =>
      issueStatuses.find(
        (s) => s.value === GetIssueResponseDtoStatusEnum.InProgress,
      ),
    [issueStatuses],
  );
  const issueStatusToBeApproved = useMemo(
    () =>
      issueStatuses.find(
        (s) => s.value === GetIssueResponseDtoStatusEnum.ToBeApproved,
      ),
    [issueStatuses],
  );
  const issueStatusOpen = useMemo(
    () =>
      issueStatuses.find((s) => s.value === GetIssueResponseDtoStatusEnum.Open),
    [issueStatuses],
  );

  const handleFilterSelection = (type: string) => {
    clearFilters();
    switch (type) {
      case OwnedIssuesTypeEnum.ALL:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          [ISSUE_FIELD_OWNER]: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            [ISSUE_FIELD_OWNER]: [userDataForFiltering],
          }),
          'quick-filter',
        );
        break;
      case OwnedIssuesTypeEnum.ACTIONS_NOT_INITIATED:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          [ISSUE_FIELD_OWNER]: true,
          [ISSUE_FIELD_ACTION_PROGRESS]: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            [ISSUE_FIELD_OWNER]: [userDataForFiltering],
            [ISSUE_FIELD_ACTION_PROGRESS]: [actionProgressNotInitiated],
          }),
          'quick-filter',
        );
        break;
      case OwnedIssuesTypeEnum.ACTIONS_INITIATED:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          [ISSUE_FIELD_OWNER]: true,
          [ISSUE_FIELD_ACTION_PROGRESS]: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            [ISSUE_FIELD_OWNER]: [userDataForFiltering],
            [ISSUE_FIELD_ACTION_PROGRESS]: [actionProgressInitiated],
          }),
          'quick-filter',
        );
        break;
      case OwnedIssuesTypeEnum.ONGOING:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          [ISSUE_FIELD_OWNER]: true,
          [ISSUE_FIELD_ACTION_PROGRESS]: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            [ISSUE_FIELD_OWNER]: [userDataForFiltering],
            [ISSUE_FIELD_ACTION_PROGRESS]: [actionProgressOngoing],
          }),
          'quick-filter',
        );
        break;
      case OwnedIssuesTypeEnum.TO_BE_APPROVED:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          [ISSUE_FIELD_OWNER]: true,
          [ISSUE_FIELD_ISSUE_STATUS]: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            [ISSUE_FIELD_OWNER]: [userDataForFiltering],
            [ISSUE_FIELD_ISSUE_STATUS]: [issueStatusToBeApproved],
          }),
          'quick-filter',
        );
        break;
      case OwnedIssuesTypeEnum.OVERDUE:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          [ISSUE_FIELD_OWNER]: true,
          [ISSUE_FIELD_DUE_DATE]: true,
          [ISSUE_FIELD_ISSUE_STATUS]: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            [ISSUE_FIELD_OWNER]: [userDataForFiltering],
            [ISSUE_FIELD_DUE_DATE]: [null, new Date()],
            [ISSUE_FIELD_ISSUE_STATUS]: [
              issueStatusInProgress,
              issueStatusToBeApproved,
              issueStatusOpen,
            ],
          }),
          'quick-filter',
        );
        break;
    }
  };

  return (
    <QuickFilterContainer label={t('quickFilters.ownedIssues.title')}>
      <QuickFilterItem
        label={t('quickFilters.ownedIssues.all')}
        number={ownedIssues?.all}
        onClick={() => {
          handleFilterSelection(OwnedIssuesTypeEnum.ALL);
          setActiveFilter(OwnedIssuesEnum.ALL);
        }}
        isActive={activeFilter === OwnedIssuesEnum.ALL}
      />
      <QuickFilterItem
        label={t('quickFilters.ownedIssues.actionsNotInitiated')}
        number={ownedIssues?.actionsNotInitiated}
        onClick={() => {
          handleFilterSelection(OwnedIssuesTypeEnum.ACTIONS_NOT_INITIATED);
          setActiveFilter(OwnedIssuesEnum.ACTIONS_NOT_INITIATED);
        }}
        isActive={activeFilter === OwnedIssuesEnum.ACTIONS_NOT_INITIATED}
      />
      <QuickFilterItem
        label={t('quickFilters.ownedIssues.actionsInitiated')}
        number={ownedIssues?.actionsInitiated}
        onClick={() => {
          handleFilterSelection(OwnedIssuesTypeEnum.ACTIONS_INITIATED);
          setActiveFilter(OwnedIssuesEnum.ACTIONS_INITIATED);
        }}
        isActive={activeFilter === OwnedIssuesEnum.ACTIONS_INITIATED}
      />
      <QuickFilterItem
        label={t('quickFilters.ownedIssues.ongoing')}
        number={ownedIssues?.actionsOngoing}
        onClick={() => {
          handleFilterSelection(OwnedIssuesTypeEnum.ONGOING);
          setActiveFilter(OwnedIssuesEnum.ONGOING);
        }}
        isActive={activeFilter === OwnedIssuesEnum.ONGOING}
      />
      <QuickFilterItem
        label={t('quickFilters.ownedIssues.toBeApproved')}
        number={ownedIssues?.toBeApproved}
        onClick={() => {
          handleFilterSelection(OwnedIssuesTypeEnum.TO_BE_APPROVED);
          setActiveFilter(OwnedIssuesEnum.TO_BE_APPROVED);
        }}
        isActive={activeFilter === OwnedIssuesEnum.TO_BE_APPROVED}
      />
      <QuickFilterItem
        label={t('quickFilters.ownedIssues.overdue')}
        number={ownedIssues?.overdue}
        onClick={() => {
          handleFilterSelection(OwnedIssuesTypeEnum.OVERDUE);
          setActiveFilter(OwnedIssuesEnum.OVERDUE);
        }}
        isActive={activeFilter === OwnedIssuesEnum.OVERDUE}
      />
    </QuickFilterContainer>
  );
}
